import { BEIGE_600, GRAY_900 } from 'constants/styling/theme'
import { FC, Fragment, useCallback } from 'react'
import { Stack, Typography } from '@mui/material'
import { ToolBarType, useClientGallery } from '../_main'

import { CircleIcon } from 'components/common/CircleIcon'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import { GalleryFloatingToolBar } from 'components/common/Gallery/GalleryFloatingToolBar'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { ProductKind } from 'constants/product'
import { ToolbarMode } from './ClientGalleryFloatingToolBar.module'
import { getProductKindIcon } from 'utils/helpers/getProductKindIcon'
import { useGalleryVisualSelection } from '../../contexts'
import { useTranslation } from 'react-i18next'

enum UploadTypes {
  PHOTO = 'PHOTO',
  FLOOR_PLAN = 'FLOOR_PLAN',
  VIDEO = 'VIDEO',
}

/**
 * @interface Props
 */
interface Props {
  /** Mode of the floating bar */
  mode?: ToolbarMode
}

/**
 * Client gallery upload floating toolbar.
 * 
 * @example <ClientGalleryUploadFloatingToolBar />
 */
export const ClientGalleryUploadFloatingToolBar: FC<Props> = ({
  mode,
}) => {
  const { t } = useTranslation(['gallery'])
  const { selectAllPurchased } = useGalleryVisualSelection()
  const {
    isToolBarOpen,
    setIsToolBarOpen,
    setToolBarType,
    setIsUploadPopupOpened
  } = useClientGallery()

  const getUploadTypeIcon = useCallback((type: UploadTypes) => {
    switch (type) {
      case UploadTypes.PHOTO:
        return getProductKindIcon(ProductKind.GROUND_PHOTO)
      case UploadTypes.FLOOR_PLAN:
        return getProductKindIcon(ProductKind.FLOOR_PLAN_EDITING_2D)
      case UploadTypes.VIDEO:
        return getProductKindIcon(ProductKind.STANDARD_GROUND_VIDEO)
      default:
        return getProductKindIcon(ProductKind.GROUND_PHOTO)
    }
  }, [])

  return (
    <GalleryFloatingToolBar
      width="auto"
      isToolBarOpen={isToolBarOpen && mode === ToolbarMode.UPLOAD_VISUALS}
      openToolBarButtonLabel={t('select_visuals')}
      onOpenToolBar={() => setIsToolBarOpen(true)}
      contentActionContainer={
        <Fragment>

          <MUIDropdown
            button={(isOpen, action) => (
              <MUIButton
                type="darkSecondaryNoborder"
                startIcon={<CloudUploadOutlinedIcon fontSize="large" />}
                onClick={action}
              >
                {t('upload_visuals_button')}
              </MUIButton>
            )}
          >
            {Object.values(UploadTypes).map((type) => (
              <MUIDropdownItem key={type} onClick={() => setIsUploadPopupOpened(true)}>
                <Stack gap=".8rem" alignItems="center" flexDirection="row">
                  <CircleIcon
                    size="2rem"
                    circleColor={BEIGE_600}
                    icon={getUploadTypeIcon(type)}
                  />
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`upload_visuals_type.${type}`)}
                  </Typography>
                </Stack>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>

          <MUIButton
            type="darkPrimary"
            startIcon={<CloudDownloadOutlinedIcon fontSize="large" />}
            onClick={() => {
              selectAllPurchased()
              setToolBarType(ToolBarType.DOWNLOAD)
              setIsToolBarOpen(true)
            }}
          >
            {t('download_visuals')}
          </MUIButton>

        </Fragment>
      }

    />
  )
}
